function renderListFieldDynamicListFieldTemplate({
  indent,
  fieldName,
  label,
  hideLabelProp,
  descriptionProp,
  fieldMaxItemsContent
}: {
  indent: string
  fieldName: string
  label: string
  hideLabelProp: string
  descriptionProp: string
  fieldMaxItemsContent: string
}): string {
  return `${indent}<DynamicListField
${indent}  name="${fieldName}"
${indent}  label="${label}"${hideLabelProp}${descriptionProp}
${indent}  disabled={isPending}${fieldMaxItemsContent}
${indent}  placeholder="Enter value"
${indent}/>`
}

function renderListFieldCardContentTemplate({
  indent,
  formItemProps,
  fieldName,
  toLowerCase,
  validateExpandedItemExpression,
  defaultItem,
  pascalFieldName,
  singularLabel,
  fieldLabelFieldLabel,
  label,
  listHintJsxContent,
  fieldMaxItemsFieldName,
  accordionTitle,
  nestedFieldsJsx
}: {
  indent: string
  formItemProps: string
  fieldName: string
  toLowerCase: string
  validateExpandedItemExpression: string
  defaultItem: string
  pascalFieldName: string
  singularLabel: string
  fieldLabelFieldLabel: string
  label: string
  listHintJsxContent: string
  fieldMaxItemsFieldName: string
  accordionTitle: string
  nestedFieldsJsx: string
}): string {
  return `${indent}<FormItem${formItemProps}>
${indent}  {${fieldName}FieldArray.fields.length === 0 && (
${indent}    <Card>
${indent}      <CardContent className="flex flex-col items-center justify-center bg-background gap-3">
${indent}        <p>No ${toLowerCase} added yet.</p>
${indent}        <Button
${indent}          type="button"
${indent}          variant="outline"
${indent}          onClick={async () => {
${indent}            if (${fieldName}Expanded) {
${indent}              const expandedIndex = Number(${fieldName}Expanded.replace('item-', '')) - 1
${indent}              if (expandedIndex >= 0) {
${indent}                const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}                if (!isExpandedItemValid) return
${indent}              }
${indent}            }
${indent}            ${fieldName}FieldArray.append(${defaultItem})
${indent}            set${pascalFieldName}Expanded(\`item-\${${fieldName}FieldArray.fields.length + 1}\`)
${indent}          }}
${indent}          disabled={isPending}
${indent}        >
${indent}          <Plus className="size-3" />
${indent}          Add ${singularLabel}
${indent}        </Button>
${indent}      </CardContent>
${indent}    </Card>
${indent}  )}

${indent}  {${fieldName}FieldArray.fields.length > 0 && (
${indent}    <div className="space-y-4">
${indent}      <div className="flex items-center justify-between gap-3">
${indent}        <FormField
${indent}          control={form.control}
${indent}          name="${fieldName}"
${indent}          render={() => (
${indent}            <FormItem className="flex flex-col gap-1 space-y-0">
${indent}              <FormLabel className="${fieldLabelFieldLabel}">${label}</FormLabel>
${listHintJsxContent}${indent}            </FormItem>
${indent}          )}
${indent}        />
${indent}        <Button
${indent}          type="button"
${indent}          variant="outline"
${indent}          onClick={async () => {
${indent}            if (${fieldName}Expanded) {
${indent}              const expandedIndex = Number(${fieldName}Expanded.replace('item-', '')) - 1
${indent}              if (expandedIndex >= 0) {
${indent}                const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}                if (!isExpandedItemValid) return
${indent}              }
${indent}            }
${indent}            ${fieldName}FieldArray.append(${defaultItem})
${indent}            set${pascalFieldName}Expanded(\`item-\${${fieldName}FieldArray.fields.length + 1}\`)
${indent}          }}
${indent}          disabled={isPending${fieldMaxItemsFieldName}}
${indent}        >
${indent}          <Plus className="size-3" />
${indent}          Add ${singularLabel}
${indent}        </Button>
${indent}      </div>

${indent}      <Accordion
${indent}        value={${fieldName}Expanded ? [${fieldName}Expanded] : []}
${indent}        onValueChange={async (nextValue) => {
${indent}          const nextExpanded = nextValue[0] ?? ''
${indent}          if (${fieldName}Expanded && nextExpanded && nextExpanded !== ${fieldName}Expanded) {
${indent}            const expandedIndex = Number(${fieldName}Expanded.replace('item-', '')) - 1
${indent}            if (expandedIndex >= 0) {
${indent}              const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}              if (!isExpandedItemValid) return
${indent}            }
${indent}          }
${indent}          set${pascalFieldName}Expanded(nextExpanded)
${indent}        }}
${indent}      >
${indent}        {${fieldName}FieldArray.fields.map((item, index) => (
${indent}          <AccordionItem key={item.id} value={\`item-\${index + 1}\`}>
${indent}            <div className="relative">
${indent}              <AccordionTrigger className="flex items-center w-full gap-2 py-2 min-h-12">
${indent}                <span className="w-full pr-16">${accordionTitle}</span>
${indent}              </AccordionTrigger>
${indent}              <Button
${indent}                type="button"
${indent}                variant="ghost"
${indent}                size="icon-sm"
${indent}                className="absolute right-10 top-1/2 -translate-y-1/2 shrink-0"
${indent}                aria-label="Remove ${singularLabel}"
${indent}                onClick={() => {
${indent}                  if (isPending) return
${indent}                  ${fieldName}FieldArray.remove(index)
${indent}                }}
${indent}                disabled={isPending}
${indent}              >
${indent}                <X />
${indent}              </Button>
${indent}            </div>
${indent}            <AccordionContent>
${indent}              <div className="space-y-4 pt-4 border-t border-border">
${nestedFieldsJsx}
${indent}              </div>
${indent}            </AccordionContent>
${indent}          </AccordionItem>
${indent}        ))}
${indent}      </Accordion>
${indent}    </div>
${indent}  )}
${indent}</FormItem>`
}

export function renderListFieldRenderer(
  request:
    | {
        kind: 'renderListFieldDynamicListFieldTemplate'
        props: {
          indent: string
          fieldName: string
          label: string
          hideLabelProp: string
          descriptionProp: string
          fieldMaxItemsContent: string
        }
      }
    | {
        kind: 'renderListFieldCardContentTemplate'
        props: {
          indent: string
          formItemProps: string
          fieldName: string
          toLowerCase: string
          validateExpandedItemExpression: string
          defaultItem: string
          pascalFieldName: string
          singularLabel: string
          fieldLabelFieldLabel: string
          label: string
          listHintJsxContent: string
          fieldMaxItemsFieldName: string
          accordionTitle: string
          nestedFieldsJsx: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderListFieldDynamicListFieldTemplate':
      return renderListFieldDynamicListFieldTemplate(request.props)
    case 'renderListFieldCardContentTemplate':
      return renderListFieldCardContentTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
